<template>
  <div class="kitchen-page">
    <header class="kitchen-header">
      <div class="kitchen-header__brand">
        <img :src="logoUrl" :alt="brandName" class="kitchen-header__logo" />
        <div class="kitchen-header__titles">
          <h1 class="kitchen-header__title">Kitchen Display</h1>
          <p class="kitchen-header__subtitle">
            <span class="kitchen-header__live" aria-hidden="true"></span>
            Live · updated {{ lastUpdatedLabel }}
          </p>
        </div>
      </div>

      <div class="kitchen-header__actions">
        <KotNotifications />
        <button
          type="button"
          class="kitchen-header__btn kitchen-header__btn--ghost"
          :title="dark ? 'Switch to light mode' : 'Switch to dark mode'"
          :aria-label="dark ? 'Switch to light mode' : 'Switch to dark mode'"
          @click="onToggleDark"
        >
          <i :class="dark ? 'fas fa-sun' : 'fas fa-moon'" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          class="kitchen-header__btn kitchen-header__btn--ghost"
          :disabled="loading"
          @click="fetchKitchenOrders"
        >
          <i class="icon_refresh" aria-hidden="true"></i>
          Refresh
        </button>
        <AccountMenu :name="displayName" @logout="signOut">
          <template v-slot="{ toggle, open }">
            <button
              type="button"
              class="kitchen-header__user"
              :title="displayName"
              aria-label="Account menu"
              aria-haspopup="menu"
              :aria-expanded="open ? 'true' : 'false'"
              @click="toggle"
            >
              <span class="kitchen-header__user-name">{{ displayName }}</span>
              <span class="kitchen-header__avatar">{{ userInitials }}</span>
            </button>
          </template>
        </AccountMenu>
      </div>
    </header>

    <main class="kitchen-main">
      <nav class="kitchen-tabs" aria-label="Order filters">
        <button
          v-for="tab in tabs"
          :key="tab"
          type="button"
          class="kitchen-tabs__btn"
          :class="{ 'kitchen-tabs__btn--active': activeTab === tab }"
          @click="activeTab = tab"
        >
          {{ tab }}
          <span v-if="tabCount(tab)" class="kitchen-tabs__count">{{ tabCount(tab) }}</span>
        </button>
      </nav>

      <div v-if="loading && !orders.length" class="kitchen-loading">
        <div v-for="n in 6" :key="n" class="kitchen-card kitchen-card--skeleton"></div>
      </div>

      <div v-else-if="visibleOrders.length" class="kitchen-grid">
        <article
          v-for="order in visibleOrders"
          :key="order.id"
          class="kitchen-card"
        >
          <div class="kitchen-card__status">
            <span class="kitchen-card__badge" :class="statusBadgeClass(order.status)">
              {{ formatStatus(order.status) }}
            </span>
          </div>

          <div class="kitchen-card__head">
            <div>
              <h2 class="kitchen-card__number">#{{ order.order_number }}</h2>
              <time class="kitchen-card__time">{{ order.displayDate }}</time>
            </div>
            <div
              v-if="order.status === 'pending' || order.status === 'preparing'"
              class="kitchen-card__elapsed"
            >
              <span class="kitchen-card__elapsed-dot"></span>
              {{ getElapsedTime(order.rawDate) }}
            </div>
          </div>

          <span
            class="kitchen-card__type"
            :class="{ 'kitchen-card__type--web': order.types[0] === 'Website' }"
          >{{ order.types[0] }}</span>

          <div class="kitchen-card__divider"></div>

          <div class="kitchen-card__items-head">
            <span>Items</span>
            <span>Qty</span>
          </div>
          <ul class="kitchen-card__items">
            <li v-for="(item, i) in order.items" :key="`${order.id}-${i}`">
              <span>{{ item.name }}</span>
              <span>{{ item.qty }}</span>
            </li>
          </ul>

          <div v-if="order.status !== 'finished'" class="kitchen-card__actions">
            <button
              v-if="order.status === 'pending'"
              type="button"
              class="kitchen-card__action kitchen-card__action--start"
              @click="updateStatus(order.id, 'preparing')"
            >
              Start
            </button>
            <button
              v-if="order.status === 'preparing'"
              type="button"
              class="kitchen-card__action kitchen-card__action--finish"
              @click="updateStatus(order.id, 'finished')"
            >
              Finish
            </button>
          </div>
        </article>
      </div>

      <div v-else class="kitchen-empty">
        <i class="icon-food_icon_cloche kitchen-empty__icon" aria-hidden="true"></i>
        <p>No {{ activeTab === 'All' ? '' : activeTab.toLowerCase() }} orders right now</p>
      </div>
    </main>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import moment from "moment";
import { mapGetters, mapState } from "vuex";
import { isKotDark, toggleKotDark } from "@/utils/kotDarkMode";
import AccountMenu from "@/components/Share/AccountMenu/index.vue";
import KotNotifications from "@/components/Share/KotNotifications/index.vue";

export default {
  name: "KitchenOrders",
  components: { AccountMenu, KotNotifications },
  data() {
    return {
      tabs: ["All", "Pending", "Preparing", "Finished"],
      activeTab: "All",
      orders: [],
      loading: false,
      pollingInterval: null,
      clockInterval: null,
      now: moment(),
      lastUpdated: null,
      dark: false,
    };
  },
  computed: {
    ...mapState("auth", ["status"]),
    ...mapGetters("auth", ["GetUser", "GetUserName"]),
    ...mapGetters("organization", ["logoUrl", "brandName"]),
    filteredOrders() {
      if (this.activeTab.toLowerCase() === "all") return this.orders;
      return this.orders.filter(
        (order) => order.status.toLowerCase() === this.activeTab.toLowerCase(),
      );
    },
    visibleOrders() {
      return this.filteredOrders;
    },
    displayName() {
      const name = this.GetUserName;
      return name || "Kitchen";
    },
    userInitials() {
      const user = this.GetUser;
      if (user && user.username) {
        const name = user.username.trim();
        return name.slice(0, 2).toUpperCase();
      }
      return "K";
    },
    lastUpdatedLabel() {
      if (!this.lastUpdated) return "just now";
      return this.lastUpdated.from(this.now);
    },
  },
  created() {
    this.dark = isKotDark();
    this.fetchKitchenOrders();
    this.pollingInterval = setInterval(() => this.fetchKitchenOrders(true), 15000);
    this.clockInterval = setInterval(() => {
      this.now = moment();
    }, 30000);
    if (!this.status) {
      this.$store.dispatch("auth/fetchUserInfo").catch(() => {});
    }
    this.$store.dispatch("organization/fetchOrganization").catch(() => {});
  },
  beforeDestroy() {
    if (this.pollingInterval) clearInterval(this.pollingInterval);
    if (this.clockInterval) clearInterval(this.clockInterval);
  },
  methods: {
    onToggleDark() {
      this.dark = toggleKotDark();
    },
    tabCount(tab) {
      if (tab === "All") return this.orders.length || 0;
      return this.orders.filter(
        (o) => o.status.toLowerCase() === tab.toLowerCase(),
      ).length;
    },
    async fetchKitchenOrders(silent = false) {
      if (!silent) this.loading = true;
      try {
        const response = await axios.get("/hms/web/orders/kitchen/orders", {
          params: { _t: Date.now() },
        });
        const payload = response.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];
        this.orders = list.map((order) => this.mapOrder(order));
        this.lastUpdated = moment();
      } catch (err) {
        console.error("Error fetching kitchen orders:", err);
      } finally {
        this.loading = false;
      }
    },
    getTicketBatchNumber(ticket) {
      if (!ticket) return 0;
      const n = parseFloat(String(ticket).slice(13));
      return Number.isFinite(n) ? n : 0;
    },
    getLatestTicket(items) {
      const ticketed = (items || []).filter((item) => item.ticket_number);
      if (!ticketed.length) return null;
      return ticketed.reduce(
        (best, item) =>
          this.getTicketBatchNumber(item.ticket_number) >=
          this.getTicketBatchNumber(best)
            ? item.ticket_number
            : best,
        ticketed[0].ticket_number,
      );
    },
    filterKitchenLineItems(items) {
      const rows = Array.isArray(items) ? items : [];
      const isActive = (item) => {
        const status = (item.status || "").toLowerCase();
        return !status || status === "pending" || status === "cooking";
      };
      const active = rows.filter(isActive);
      if (!active.length) return [];

      const maxCookedId = rows
        .filter((item) => (item.status || "").toLowerCase() === "cooked")
        .reduce((max, item) => Math.max(max, Number(item.id) || 0), 0);

      if (maxCookedId > 0) {
        const newest = active.filter((item) => (Number(item.id) || 0) > maxCookedId);
        if (newest.length) return newest;
      }

      const latestTicket = this.getLatestTicket(rows);
      if (latestTicket) {
        return active.filter((item) => item.ticket_number === latestTicket);
      }

      return active;
    },
    mapOrder(order) {
      const batchAt = order.kitchen_batch_at || order.created_at;
      const kitchenItems = this.filterKitchenLineItems(order.items || []);
      const batchAtFromItems = kitchenItems.reduce((latest, item) => {
        if (!item.created_at) return latest;
        const ts = moment.utc(item.created_at);
        return !latest || ts.isAfter(latest) ? ts : latest;
      }, null);
      const displayMoment = batchAtFromItems || moment.utc(batchAt).local();
      return {
        id: order.id,
        rawDate: displayMoment,
        displayDate: displayMoment.format("h:mm a"),
        order_number: order.order_number,
        status: (order.kitchen_status || "pending").toLowerCase(),
        types: [
          String(order.note || "").indexOf("Website pickup") === 0
            ? "Website"
            : this.getOrderTypeLabel(order.order_type),
        ],
        items: kitchenItems.map((item) => ({
          name: item.dish
            ? item.dish.name
            : item.product
            ? item.product.name
            : item.name || "-",
          qty: item.qty || item.quantity || 0,
        })),
      };
    },
    async updateStatus(orderId, newStatus) {
      try {
        await axios.patch(`/hms/web/orders/${orderId}/kitchen-status`, {
          kitchen_status: newStatus,
        });
        this.orders = this.orders.map((order) =>
          order.id === orderId
            ? { ...order, status: newStatus }
            : order,
        );
        await this.fetchKitchenOrders(true);
      } catch (err) {
        console.error("Error updating status:", err);
      }
    },
    signOut() {
      this.$store.dispatch("auth/LogOut");
      this.$router.push("/kitchen/login");
    },
    statusBadgeClass(status) {
      switch ((status || "").toLowerCase()) {
        case "finished":
          return "kitchen-card__badge--finished";
        case "preparing":
          return "kitchen-card__badge--preparing";
        default:
          return "kitchen-card__badge--pending";
      }
    },
    formatStatus(status) {
      if (!status) return "Pending";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    getOrderTypeLabel(type) {
      switch (type) {
        case "dine_in":
          return "Dine In";
        case "take_away":
          return "Take Away";
        case "service_order":
          return "Room Order";
        default:
          return type || "Order";
      }
    },
    getElapsedTime(orderDate) {
      const duration = moment.duration(this.now.diff(orderDate));
      const minutes = Math.floor(duration.asMinutes());
      const hours = Math.floor(duration.asHours());
      return hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes} mins`;
    },
  },
};
</script>

<style scoped>
.kitchen-page {
  min-height: 100vh;
  background: #f1f5f9;
  text-align: left;
}

.kitchen-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.kitchen-header__brand {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.kitchen-header__logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
  flex-shrink: 0;
}

.kitchen-header__titles {
  min-width: 0;
}

.kitchen-header__title {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.kitchen-header__subtitle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 2px 0 0;
  font-size: 12px;
  color: #64748b;
}

.kitchen-header__live {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
  animation: kitchen-pulse 1.4s ease-out infinite;
}

@keyframes kitchen-pulse {
  0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55); }
  70% { box-shadow: 0 0 0 8px rgba(34, 197, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
}

.kitchen-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.kitchen-header__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.kitchen-header__btn--ghost {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
}

.kitchen-header__btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
}

.kitchen-header__user {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 38px;
  padding: 4px 6px 4px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s;
}

.kitchen-header__user:hover {
  background: #f8fafc;
}

.kitchen-header__user-name {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.kitchen-header__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #3b82f6;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.kitchen-main {
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
}

.kitchen-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.kitchen-tabs__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  color: #334155;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.kitchen-tabs__btn--active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.28);
}

.kitchen-tabs__count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}

.kitchen-tabs__btn:not(.kitchen-tabs__btn--active) .kitchen-tabs__count {
  background: #e2e8f0;
  color: #475569;
}

.kitchen-grid,
.kitchen-loading {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.kitchen-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 22px 20px 18px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  transition: transform 0.15s, box-shadow 0.15s;
}

.kitchen-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.kitchen-card--skeleton {
  min-height: 220px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: kitchen-shimmer 1.2s infinite;
}

@keyframes kitchen-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.kitchen-card__status {
  position: absolute;
  top: 16px;
  right: 16px;
}

.kitchen-card__badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.kitchen-card__badge--pending {
  background: #fef3c7;
  color: #b45309;
}

.kitchen-card__badge--preparing {
  background: #dbeafe;
  color: #1d4ed8;
}

.kitchen-card__badge--finished {
  background: #dcfce7;
  color: #15803d;
}

.kitchen-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding-right: 112px;
}

.kitchen-card__number {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.kitchen-card__time {
  display: block;
  margin-top: 2px;
  font-size: 13px;
  color: #64748b;
}

.kitchen-card__elapsed {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #dc2626;
  white-space: nowrap;
}

.kitchen-card__elapsed-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #dc2626;
}

.kitchen-card__type {
  display: inline-block;
  margin-top: 10px;
  padding: 4px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.kitchen-card__type--web {
  border-color: #93c5fd;
  background: #dbeafe;
  color: #1d4ed8;
}

.kitchen-card__divider {
  height: 1px;
  margin: 14px 0 12px;
  background: #e2e8f0;
}

.kitchen-card__items-head,
.kitchen-card__items li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
}

.kitchen-card__items-head {
  margin-bottom: 6px;
  color: #64748b;
  font-weight: 600;
}

.kitchen-card__items {
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
}

.kitchen-card__items li {
  padding: 5px 0;
  color: #0f172a;
  border-bottom: 1px solid #f1f5f9;
}

.kitchen-card__items li:last-child {
  border-bottom: none;
}

.kitchen-card__actions {
  margin-top: auto;
  padding-top: 4px;
}

.kitchen-card__action {
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;
}

.kitchen-card__action--start {
  background: #f59e0b;
  color: #1f2937;
}

.kitchen-card__action--finish {
  background: #22c55e;
  color: #fff;
}

.kitchen-card__action:hover {
  opacity: 0.92;
}

.kitchen-empty {
  padding: 60px 20px;
  text-align: center;
  color: #64748b;
}

.kitchen-empty__icon {
  display: block;
  margin: 0 auto 12px;
  font-size: 36px;
  color: #94a3b8;
}

@media (max-width: 640px) {
  .kitchen-header {
    flex-direction: column;
    align-items: stretch;
  }

  .kitchen-header__actions {
    justify-content: space-between;
  }

  .kitchen-header__user-name {
    max-width: 80px;
  }

  .kitchen-main {
    padding: 14px;
  }

  .kitchen-grid,
  .kitchen-loading {
    grid-template-columns: 1fr;
  }
}
</style>
